/**
 * PropertyMapCard - Mapbox map showing property location
 *
 * Features:
 * - Mapbox GL map with property marker
 * - Address display
 * - Station proximity info
 */
import { useMemo } from "react";
import Map, { Marker } from "react-map-gl/mapbox";
import { MapPin, Train } from "lucide-react";
import { Card } from "@/shared/ui/components";
import { env } from "@/shared/config/env";
import { useTranslation } from "@/shared/hooks";
import "mapbox-gl/dist/mapbox-gl.css";

interface PropertyMapCardProps {
  geo: {
    lat: number;
    lng: number;
  };
  address: string;
  station?: string;
  walkMinutes?: number;
  lineName?: string;
}

export function PropertyMapCard({
  geo,
  address,
  station,
  walkMinutes,
  lineName,
}: PropertyMapCardProps) {
  const t = useTranslation();

  const initialViewState = useMemo(
    () => ({
      longitude: geo.lng,
      latitude: geo.lat,
      zoom: 15,
    }),
    [geo.lat, geo.lng],
  );

  return (
    <Card variant="default" padding="lg" className="shadow-sm">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        {t("listingDetailLocation")}
      </h3>

      {/* Station Info - MORE PROMINENT */}
      {station && (
        <div className="flex items-center gap-3 mb-4 p-3 bg-primary-50 rounded-lg border border-primary-100">
          <Train className="w-5 h-5 text-primary-600 flex-shrink-0" />
          <div>
            <p className="font-semibold text-gray-900">
              {station}
              {lineName && ` (${lineName})`} {t("listingDetailStation")}
            </p>
            {walkMinutes && (
              <p className="text-sm text-gray-600">
                {walkMinutes} {t("listingDetailStationWalk")}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Address */}
      <div className="flex items-start gap-2 mb-4">
        <MapPin className="w-5 h-5 text-primary-500 mt-0.5 flex-shrink-0" />
        <p className="text-gray-700">{address}</p>
      </div>

      {/* Map */}
      <div className="relative h-[300px] w-full rounded-lg overflow-hidden border border-gray-200">
        <Map
          mapboxAccessToken={env.mapboxToken}
          initialViewState={initialViewState}
          style={{ width: "100%", height: "100%" }}
          mapStyle="mapbox://styles/mapbox/streets-v12"
          attributionControl={false}
        >
          {/* Property Marker */}
          <Marker longitude={geo.lng} latitude={geo.lat} anchor="bottom">
            <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center shadow-lg border-4 border-white">
              <MapPin className="w-5 h-5 text-white" />
            </div>
          </Marker>
        </Map>
      </div>

      {/* Coordinates */}
      <div className="mt-2 text-xs text-gray-400 text-center">
        {geo.lat.toFixed(6)}, {geo.lng.toFixed(6)}
      </div>
    </Card>
  );
}
